import { logger } from '@/utils/logger/logger';
import { isValidPath } from '@/utils/urls';

export enum HTTPMethod {
    POST = 'POST',
    GET = 'GET',
    DELETE = 'DELETE',
}

export type InvokeArgs = {
    path: string;
    query?: any;
    body?: any;
    method?: HTTPMethod;
    replacements?: { [key: string]: string | number | string[] };
    keepalive?: boolean;
};

export type IdempotentArgs = Omit<InvokeArgs, 'method' | 'body'> | string;
export type PostArgs = Omit<InvokeArgs, 'method'>;
export type MultiCall = IdempotentArgs | Promise<unknown> | (() => Promise<unknown>) | null;

export type HttpClient = {
    /**
     * Make a single GET request. Can be a simple URL string or more complex query
     */
    get<T = any>(args: IdempotentArgs | string): Promise<T | null>;
    /**
     * Make a POST request.
     */
    post<T = any>(args: PostArgs): Promise<T | null>;
    /**
     * Make a DELETE request.
     */
    delete<T = any>(args: IdempotentArgs): Promise<T | null>;
    /**
     * Make an API search request. Body parameters will be sanitized
     */
    search<T = any>(args: PostArgs): Promise<T | null>;

    /**
     * A flexible function for cleanly making lots of GET requests at once.
     * All are required to be successful
     */
    getAll(endpoints: MultiCall[]): Promise<Array<any>>;
    /**
     * A flexible function for cleanly making lots of GET requests at once.
     * Will not throw if one fails, will instead return undefined for that request
     */
    getOptional(endpoints: MultiCall[]): Promise<Array<any>>;
};

export const parsePath = (path: InvokeArgs['path'], replacements: InvokeArgs['replacements'] = {}): string | null => {
    let fullPath = path;
    Object.keys(replacements).forEach((key) => {
        if (key && replacements[key]) {
            const replacement = encodeURIComponent(replacements[key].toString());
            fullPath = fullPath
                .replace(`[${key.toLowerCase()}]`, replacement)
                .replace(`[${key.toUpperCase()}]`, replacement);
        }
    });
    if (!isValidPath(fullPath)) {
        logger.info('[HttpClient] Path is not valid: ' + path);
        return null;
    }
    if (fullPath.includes('?')) {
        logger.warn('[HttpClient] path includes query string, switch to use query attribute', { path, fullPath });
    }
    return fullPath;
};

export const parseIdempotentArgs = (args: IdempotentArgs): InvokeArgs => {
    let path;
    let query;
    let replacements: InvokeArgs['replacements'] = {};
    if (typeof args === 'string') {
        path = args;
    } else if (typeof args === 'object') {
        ({ path, query, replacements = {} } = args as InvokeArgs);
    }

    return {
        path,
        query,
        replacements,
        method: HTTPMethod.GET,
    };
};

export const readResponseErrorMessage = async (response: Response): Promise<string> => {
    let message;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        message = await response.json();
    } else {
        message = await response.text();
    }

    // message will either be a string or an object with detail as a property.
    // Apache return plain text, old API returns response.message, new API returns response.detail
    return message?.detail ?? message?.message ?? message?.title ?? message;
};
